import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Bell,
  ChatCircle,
  CheckCircle,
  Clock,
  Copy,
  DownloadSimple,
  FileText,
  Eye,
  Image as ImageIcon,
  Images,
  Link as LinkIcon,
  Trash,
  VideoCamera,
  ArrowSquareOut,
  Circle,
  RadioButton,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import type { BriefAsset, ClientReview, ClientVersion } from '@mybusinessmate/schema';
import { signedDownloadUrl } from '../../lib/asset-url';
import {
  captionOf,
  clientTimeline,
  dayWord,
  fileMeta,
  fileSize,
  formatText,
  goesLive,
  madeBy,
  platformsText,
  workerText,
  type TimelineEntry,
} from '../../lib/client-review-display';
import { cx } from '../../lib/cx';
import { formatDay } from '../../lib/project-display';
import { clientStatus, TASK_TYPE_META, weekdayDay } from '../../lib/task-display';
import { Icon } from '../Icon';
import { BriefAssets, BriefPreview } from '../tasks/brief-preview';
import { IconButton } from '../ui/icon-button';
import { SegmentedControl } from '../ui/segmented-control';
import { StatusTag } from '../ui/status-tag';
import { useToast } from '../ui/toast';
import { CarouselStrip, FilesPreview, ReelPreview, SitePreview, WorkingStage } from './review-stage';
import { TaskMenu } from './task-menu';
import { toasts } from '../../lib/toast-presets';

/** The cards of the client's task page (Figma C03.3): white, 24 radius, a soft lift, 22 inside. */
const CARD =
  'flex flex-col gap-(--mbm-space-3-5) rounded-(--mbm-component-radius-widget) bg-surface-card p-(--mbm-space-5-5) shadow-(--mbm-shadow-table-card)';

const BRIEF = 'brief';

/** The outlined chip beside the status ("1 of 2 change rounds used", "Nothing to review yet"). */
const OUTLINE_CHIP =
  'inline-flex h-(--mbm-space-5) items-center rounded-full border border-border-default px-(--mbm-space-2) text-label-tag text-text-secondary';

const iconFor = (asset: BriefAsset): PhosphorIcon =>
  asset.contentType.startsWith('video/') ? VideoCamera : asset.contentType.startsWith('image/') ? ImageIcon : FileText;

/** Opens each of the version's files through a signed link: the only way a file leaves storage. */
async function openAll(assets: BriefAsset[], toast: ReturnType<typeof useToast>) {
  try {
    for (const asset of assets) window.open(await signedDownloadUrl(asset.key), '_blank', 'noopener,noreferrer');
  } catch {
    toast({ title: 'Couldn’t open the files', tone: 'error' });
  }
}

function DetailsCard({ review, version }: { review: ClientReview; version: ClientVersion | null }) {
  const { task } = review;
  // Before the first version there is only the brief; what it asked for is what the card can say.
  const d = version?.details ?? review.brief?.details;
  const rows: { label: string; value: string | null }[] = [
    { label: 'Due date', value: task.deadline ? weekdayDay(task.deadline) : 'Not set' },
  ];
  if (d && (d.type === 'post' || d.type === 'reel')) {
    rows.push({
      label: 'Publishing',
      value: [platformsText(d.platforms), d.scheduledUploadAt ? goesLive(d.scheduledUploadAt) : null]
        .filter(Boolean)
        .join(' · '),
    });
    rows.push({ label: 'Format', value: formatText(review, version) });
  }
  if (version) rows.push({ label: 'Made by', value: madeBy(version) });
  else if (task.worker) rows.push({ label: 'Made by', value: workerText(task.worker) });

  return (
    <section className={CARD}>
      <h2 className="text-heading-h3 text-text-primary">Details</h2>
      <dl className="flex flex-col gap-(--mbm-space-3)">
        {rows
          .filter((r) => r.value)
          .map((row) => (
            <div key={row.label} className="flex items-baseline justify-between gap-(--mbm-space-4)">
              <dt className="text-body-small text-text-tertiary">{row.label}</dt>
              <dd className="text-right text-label-medium text-text-strong">{row.value}</dd>
            </div>
          ))}
      </dl>
    </section>
  );
}

/** Before there is anything to hand over: the card is a single line saying when files appear (Figma C03.10a). */
function DeliverablesWaiting() {
  return (
    <section className={cx(CARD, 'flex-row items-center justify-between')}>
      <h2 className="text-heading-h3 text-text-primary">Deliverables</h2>
      <span className="text-label-medium text-text-tertiary">Files appear after you approve</span>
    </section>
  );
}

function DeliverablesCard({ version }: { version: ClientVersion }) {
  const toast = useToast();
  // A carousel is one deliverable of N slides plus its cover, not N unrelated files (Figma C03.10).
  const slides = version.details.type === 'post' && version.assets.length > 1 ? version.assets : null;
  const slidesBytes = slides?.reduce((n, a) => n + a.size, 0) ?? 0;
  const caption = captionOf(version);
  const link = version.details.type === 'website' ? version.details.liveUrl : null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      toast({ title: 'Copied', tone: 'success' });
    } catch {
      toast({ title: 'Couldn’t copy', description: 'Select the text and copy it.', tone: 'error' });
    }
  };
  const row = (key: string, icon: PhosphorIcon, title: string, meta: string, action: React.ReactNode) => (
    <li key={key} className="flex items-center gap-(--mbm-space-3)">
      <span className="flex size-(--mbm-size-icon-tile) shrink-0 items-center justify-center rounded-(--mbm-component-radius-icon-tile) bg-surface-tint text-text-primary">
        <Icon icon={icon} size="md" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-label-large text-text-primary">{title}</span>
        <span className="truncate text-body-xsmall text-text-tertiary">{meta}</span>
      </span>
      {action}
    </li>
  );
  const count = version.assets.length + (caption ? 1 : 0) + (link ? 1 : 0);
  if (count === 0) return null;
  return (
    <section className={CARD}>
      <div className="flex items-center justify-between gap-(--mbm-space-3)">
        <h2 className="text-heading-h3 text-text-primary">Deliverables</h2>
        {version.assets.length > 0 ? (
          <button
            type="button"
            onClick={() => void openAll(version.assets, toast)}
            className="text-label-medium text-brand-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
          >
            Download all
          </button>
        ) : null}
      </div>
      <ul className="flex flex-col gap-(--mbm-space-3-5)">
        {link
          ? row(
              'link',
              LinkIcon,
              'Staging link',
              link,
              <a
                href={/^https?:\/\//.test(link) ? link : `https://${link}`}
                target="_blank"
                rel="noreferrer noopener"
                aria-label="Open the staging link"
                className="text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
              >
                <Icon icon={ArrowSquareOut} size="md" />
              </a>,
            )
          : null}
        {slides
          ? [
              row(
                'slides',
                Images,
                `${slides.length} slides`,
                `${(slides[0]!.name.split('.').pop() ?? '').toUpperCase()} · ${fileSize(slidesBytes)}`,
                <button
                  type="button"
                  aria-label={`Download all ${slides.length} slides`}
                  onClick={() => void openAll(slides, toast)}
                  className="text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
                >
                  <Icon icon={DownloadSimple} size="md" />
                </button>,
              ),
              row(
                'cover',
                ImageIcon,
                'Cover slide',
                fileMeta(slides[0]!),
                <button
                  type="button"
                  aria-label="Download the cover slide"
                  onClick={() => void openAll([slides[0]!], toast)}
                  className="text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
                >
                  <Icon icon={DownloadSimple} size="md" />
                </button>,
              ),
            ]
          : version.assets.map((asset) =>
              row(
                asset.id,
                iconFor(asset),
                asset.label ?? asset.name,
                fileMeta(asset),
                <button
                  type="button"
                  aria-label={`Download ${asset.name}`}
                  onClick={() => void openAll([asset], toast)}
                  className="text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
                >
                  <Icon icon={DownloadSimple} size="md" />
                </button>,
              ),
            )}
        {caption
          ? row(
              'caption',
              FileText,
              'Caption and hashtags',
              `${caption.length} characters`,
              <button
                type="button"
                aria-label="Copy the caption and hashtags"
                onClick={() => void copy()}
                className="text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
              >
                <Icon icon={Copy} size="md" />
              </button>,
            )
          : null}
      </ul>
    </section>
  );
}

/** A site's pages as the team builds them: the sections done, the one in hand, the ones still to come. */
function SectionsCard({ review, version }: { review: ClientReview; version: ClientVersion | null }) {
  const d = version?.details;
  const built = d && d.type === 'website' ? d.sections : null;
  const planned = review.brief?.details.type === 'website' ? review.brief.details.pages.flatMap((p) => p.sections) : [];
  const rows =
    built && built.length > 0
      ? built.map((s) => ({ title: s.title, done: s.built }))
      : planned.map((title) => ({ title, done: false }));
  if (rows.length === 0) return null;
  const done = rows.filter((r) => r.done).length;
  const doing = rows.findIndex((r) => !r.done);
  return (
    <section className={CARD}>
      <h2 className="flex items-baseline gap-(--mbm-space-2) text-heading-h3 text-text-primary">
        <span className="text-text-tertiary">
          {done} of {rows.length}
        </span>
        Page sections
      </h2>
      <ul className="flex flex-col gap-(--mbm-space-3)">
        {rows.map((row, i) => (
          <li key={`${row.title}-${i}`} className="flex items-center gap-(--mbm-space-3)">
            <span
              className={cx(
                row.done
                  ? 'text-feedback-success'
                  : i === doing && done > 0
                    ? 'text-brand-primary'
                    : 'text-border-strong',
              )}
            >
              <Icon icon={row.done ? CheckCircle : i === doing && done > 0 ? RadioButton : Circle} size="lg" />
            </span>
            <span className="flex-1 text-body-medium text-text-primary">{row.title}</span>
            <span className="text-body-small text-text-tertiary">
              {row.done ? 'Done' : i === doing && done > 0 ? 'In progress' : ''}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Timeline({ entries }: { entries: TimelineEntry[] }) {
  return (
    <section id="versions" className={CARD}>
      <h2 className="text-heading-h3 text-text-primary">Versions & activity</h2>
      <ul className="flex flex-col gap-(--mbm-space-4)">
        {entries.map((entry) => (
          <li key={entry.key} className="flex items-start gap-(--mbm-space-3)">
            <span
              aria-hidden
              className={cx(
                'mt-(--mbm-space-1-5) size-(--mbm-space-2) shrink-0 rounded-full',
                entry.current ? 'bg-brand-primary' : 'bg-border-default',
              )}
            />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-label-medium text-text-primary">{entry.title}</span>
              <span className="text-body-xsmall text-text-tertiary">{entry.sub}</span>
            </span>
            {entry.tag ? <StatusTag label={entry.tag.label} tone={entry.tag.tone} className="shrink-0" /> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The thing itself, in the frame the client reads it in: a post as Instagram draws it, a reel in its portrait frame. */
function Stage({ review, version }: { review: ClientReview; version: ClientVersion }) {
  const type = review.task.taskType;
  return (
    <div
      className={cx(
        'flex min-h-(--mbm-size-task-stage) items-center justify-center rounded-(--mbm-component-radius-widget) p-(--mbm-space-4)',
        type === 'website' || type === 'monthly_update' || type === 'custom' ? 'bg-surface-tint' : 'bg-action-primary',
      )}
    >
      {type === 'post' ? (
        <CarouselStrip assets={version.assets} />
      ) : type === 'reel' ? (
        <ReelPreview version={version} />
      ) : type === 'website' ? (
        <SitePreview version={version} note={false} />
      ) : (
        <FilesPreview assets={version.assets} />
      )}
    </div>
  );
}

/**
 * One of the client's tasks (Figma C03.3): where it stands, the work itself with its versions to switch between, what
 * was asked for, the files, and the history. Answering lives on the review page; this is where they come to look.
 */
export function ClientTaskPage({ review }: { review: ClientReview }) {
  const toast = useToast();
  const navigate = useNavigate();
  const { task } = review;
  const type = TASK_TYPE_META[task.taskType];
  const website = task.taskType === 'website';
  // With no version yet the page opens on "working on it"; the brief is one tap away, here and from the footer.
  const [picked, setPicked] = useState<string | null>(review.versions[0]?.id ?? null);
  const version =
    picked === BRIEF || picked === null
      ? null
      : (review.versions.find((v) => v.id === picked) ?? review.versions[0] ?? null);
  const current = review.versions[0] ?? null;
  const working = review.versions.length === 0 && picked !== BRIEF;
  const answerable = review.answerableId !== null;
  const approved = current?.clientStatus === 'approved';
  const status = answerable
    ? { label: 'Ready for your review', tone: 'blue' as const }
    : approved
      ? { label: 'Approved', tone: 'green' as const }
      : clientStatus(task.status);
  const noun = website ? 'Snapshot' : 'Version';
  const planned = (current?.details ?? review.brief?.details) as { scheduledUploadAt?: string | null } | undefined;

  const switcher = [
    ...review.versions.map((v) => ({
      value: v.id,
      label: website
        ? v.id === current?.id
          ? `Snapshot ${v.versionNumber}`
          : String(v.versionNumber)
        : `v${v.versionNumber}`,
    })),
    ...(website || !review.brief ? [] : [{ value: BRIEF, label: 'Brief' }]),
  ];

  // Under the title: where it sits and what state it is in, in the client's words (Figma C03.10 / 10a / 10b).
  const sub = (
    working
      ? [task.projectName, type.label, task.worker ? `${task.worker.name} is on it` : 'Your team is on it']
      : approved && current && version?.id === current.id
        ? [task.projectName, type.label, `approved by you on ${formatDay(current.reviewedAt ?? current.sentAt)}`]
        : [
            task.projectName,
            type.label,
            version ? `${noun} ${version.versionNumber}` : 'Your brief',
            version ? `sent ${dayWord(version.sentAt)}` : review.brief ? formatDay(review.brief.createdAt) : null,
          ]
  )
    .filter(Boolean)
    .join(' · ');

  const soon = (description: string) => () => toast(toasts.comingSoon(description));
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast({ title: 'Link copied', tone: 'success' });
    } catch {
      toast({ title: 'Couldn’t copy the link', tone: 'error' });
    }
  };

  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-(--mbm-space-4)">
        <div className="flex flex-col gap-(--mbm-space-1)">
          <Link
            to="/subdomain/app/projects/$projectId"
            params={{ projectId: task.projectId }}
            className="mb-(--mbm-space-1) inline-flex items-center gap-(--mbm-space-2) self-start text-label-medium text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
          >
            <Icon icon={ArrowLeft} size="sm" />
            {task.projectName}
          </Link>
          <h1 className="text-heading-page text-text-strong">{task.title}</h1>
          <p className="text-body-compact text-text-secondary">{sub}</p>
        </div>
        <div className="flex items-center gap-(--mbm-space-3)">
          <IconButton
            icon={ChatCircle}
            aria-label="Message your team"
            onClick={soon('Chat arrives with the messaging step.')}
          />
          <TaskMenu
            items={[
              ...(current
                ? [
                    {
                      label: 'Open in Workbench',
                      hint: 'Review and leave feedback',
                      icon: ArrowSquareOut,
                      onSelect: () =>
                        void navigate({ to: '/subdomain/app/work/review/$taskId', params: { taskId: task.id } }),
                    },
                  ]
                : []),
              { label: 'Copy link', hint: 'Share it outside the app', icon: LinkIcon, onSelect: () => void copyLink() },
              ...(current && current.assets.length > 0
                ? [
                    {
                      label: 'Download all files',
                      hint: `${current.assets.length} ${current.assets.length === 1 ? 'file' : 'files'} · ${Math.max(1, Math.round(current.assets.reduce((n, a) => n + a.size, 0) / 1024 / 1024))} MB`,
                      icon: DownloadSimple,
                      onSelect: () => void openAll(current.assets, toast),
                    },
                  ]
                : []),
              {
                label: 'Version history',
                hint: `${review.versions.length + (review.brief ? 1 : 0)} versions`,
                icon: Clock,
                onSelect: () => document.getElementById('versions')?.scrollIntoView({ behavior: 'smooth' }),
              },
              {
                label: 'Mute updates',
                hint: 'Stop alerts for this task',
                icon: Bell,
                onSelect: soon('Muting arrives with notifications.'),
              },
              {
                label: 'Report a problem',
                hint: 'Something wrong with this task',
                icon: Trash,
                danger: true,
                onSelect: soon('Reporting arrives with the messaging step.'),
              },
            ]}
          />
        </div>
      </header>

      <div className="grid items-start gap-(--mbm-space-6) lg:grid-cols-[minmax(0,660fr)_minmax(0,397fr)]">
        <section className="flex flex-col gap-(--mbm-space-5) rounded-(--mbm-component-radius-widget) bg-surface-card p-(--mbm-space-6) shadow-(--mbm-shadow-table-card)">
          <div className="flex flex-wrap items-center justify-between gap-(--mbm-space-3)">
            <div className="flex flex-wrap items-center gap-(--mbm-space-2)">
              <StatusTag label={status.label} tone={status.tone} />
              {working ? (
                <span className={OUTLINE_CHIP}>Nothing to review yet</span>
              ) : approved && planned?.scheduledUploadAt ? (
                <span className={OUTLINE_CHIP}>Goes live {goesLive(planned.scheduledUploadAt)}</span>
              ) : review.versions.length > 0 && !website ? (
                <span className={OUTLINE_CHIP}>
                  {task.revisionCount} of {task.maxRevisions} change rounds used
                </span>
              ) : null}
              {website ? <span className={OUTLINE_CHIP}>Staging only · customers can’t see it yet</span> : null}
            </div>
            {review.versions.length === 0 && review.brief ? (
              <button
                type="button"
                aria-pressed={picked === BRIEF}
                onClick={() => setPicked(picked === BRIEF ? null : BRIEF)}
                className="rounded-full bg-surface-tint p-(--mbm-space-0-5) focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
              >
                <span
                  className={cx(
                    'flex h-(--mbm-size-segmented-pill) items-center rounded-full px-(--mbm-space-3) text-label-tag',
                    picked === BRIEF ? 'bg-surface-card text-text-primary' : 'text-text-secondary',
                  )}
                >
                  Brief
                </span>
              </button>
            ) : switcher.length > 1 ? (
              <SegmentedControl shape="pill" options={switcher} value={picked ?? ''} onChange={setPicked} />
            ) : null}
          </div>

          {version ? (
            <Stage review={review} version={version} />
          ) : working ? (
            <div className="flex min-h-(--mbm-size-task-stage) items-center justify-center rounded-(--mbm-component-radius-widget) bg-surface-tint p-(--mbm-space-4)">
              <WorkingStage
                who={task.worker?.name ?? 'Your team'}
                progress={task.progress}
                expected={task.deadline ? weekdayDay(task.deadline) : null}
              />
            </div>
          ) : (
            <div className="flex min-h-(--mbm-size-task-stage) flex-col gap-(--mbm-space-4) rounded-(--mbm-component-radius-widget) bg-surface-tint p-(--mbm-space-5)">
              {review.brief ? (
                <div className="flex flex-col gap-(--mbm-space-4) rounded-(--mbm-radius-md) bg-surface-card p-(--mbm-space-5)">
                  <BriefAssets assets={review.brief.assets} />
                  <BriefPreview details={review.brief.details} />
                </div>
              ) : null}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-(--mbm-space-4)">
            <p className="text-body-small text-text-tertiary">
              {website
                ? 'A preview only. Customers can’t see it until you approve the launch.'
                : answerable
                  ? 'Approve it or ask for changes in the Workbench.'
                  : approved && current
                    ? `You approved v${current.versionNumber} on ${formatDay(current.reviewedAt ?? current.sentAt)}.`
                    : current
                      ? 'Open it in the Workbench to see your feedback and the files.'
                      : 'We’ll ping you as soon as there’s something to review.'}
            </p>
            {current ? (
              <Link
                to="/subdomain/app/work/review/$taskId"
                params={{ taskId: task.id }}
                className="inline-flex h-(--mbm-size-button-review) w-full items-center justify-center gap-(--mbm-space-2) rounded-(--mbm-component-radius-button) bg-action-primary px-(--mbm-space-6) text-label-large sm:w-(--mbm-layout-task-action-width) text-text-on-dark focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
              >
                <Icon icon={Eye} size="lg" />
                {answerable ? 'Review in Workbench' : 'Open in Workbench'}
              </Link>
            ) : review.brief ? (
              <button
                type="button"
                onClick={() => setPicked(picked === BRIEF ? null : BRIEF)}
                className="inline-flex h-(--mbm-size-button-review) w-full items-center justify-center gap-(--mbm-space-2) rounded-(--mbm-component-radius-button) bg-action-primary px-(--mbm-space-6) text-label-large sm:w-(--mbm-layout-task-action-width) text-text-on-dark focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
              >
                <Icon icon={Eye} size="lg" />
                {picked === BRIEF ? 'Back to progress' : 'View your brief'}
              </button>
            ) : null}
          </div>
        </section>

        <div className="flex flex-col gap-(--mbm-space-4)">
          {website ? <SectionsCard review={review} version={version ?? current} /> : null}
          <DetailsCard review={review} version={version ?? current} />
          {(version ?? current) ? <DeliverablesCard version={(version ?? current)!} /> : <DeliverablesWaiting />}
          <Timeline entries={clientTimeline(review)} />
        </div>
      </div>
    </>
  );
}
